import type { MentionToken } from '../../helpers';
import { Comment, User } from '../../types';

interface ReplyTarget {
  parent: Comment;
  receiverId: string;
  username: string;
}

// Users checked for one @, written "@a @b" from start to end, while the list stays open.
interface MentionGroup {
  start: number;
  end: number;
  // Search kept while users are checked, so the list doesn't change.
  query: string;
  usernames: string[];
}

interface State {
  comments: Comment[];
  hasNextPage: boolean;
  loading: boolean;
  // Parents whose replies are shown.
  expanded: string[];
  replyTo: ReplyTarget | null;
  // Draft of the field.
  message: string;
  sending: boolean;
  // The @ being typed, the one selected with the arrows and the users picked (for the mentioned ids).
  mentionToken: MentionToken | null;
  mentionGroup: MentionGroup | null;
  activeSuggestion: number;
  pickedUsers: User[];
  // Comment opened from a notification, highlighted for a moment.
  highlighted: string;
}

type Action =
  | { type: 'videoChanged' }
  | { type: 'pageRequested' }
  | { type: 'commentsLoaded'; docs: Comment[]; hasNextPage: boolean; offset: number }
  | { type: 'loadingEnded' }
  | { type: 'likeToggled'; commentId: string }
  | { type: 'replyTo'; parent: Comment; receiverId: string; username: string }
  | { type: 'cancelReply' }
  | { type: 'repliesToggled'; parentId: string }
  | { type: 'highlight'; commentId: string; parentId: string }
  | { type: 'highlightEnded' }
  | { type: 'textChanged'; text: string }
  | { type: 'mentionTokenChanged'; token: MentionToken | null }
  | { type: 'mentionsClosed' }
  | { type: 'activeSuggestionChanged'; index: number }
  | { type: 'activeSuggestionMoved'; delta: number; count: number }
  | { type: 'mentionToggled'; text: string; user: User; group: MentionGroup | null }
  | { type: 'sendStarted' }
  | { type: 'sendFailed' }
  | { type: 'commentAdded'; comment: Comment; parentId: string };

const initialState: State = {
  comments: [],
  hasNextPage: false,
  loading: true,
  expanded: [],
  replyTo: null,
  message: '',
  sending: false,
  mentionToken: null,
  mentionGroup: null,
  activeSuggestion: 0,
  pickedUsers: [],
  highlighted: '',
};

const addOnce = (list: string[], id: string) => {
  if (list.includes(id)) {
    return list;
  }
  return [...list, id];
};

// Applies a change to a comment of the tree (a parent or one of its replies).
const updateComment = (list: Comment[], id: string, change: (c: Comment) => Comment) => list.map((c) => {
  if (c.id === id) {
    return change(c);
  }
  if (c.comments && c.comments.some((r) => r.id === id)) {
    return { ...c, comments: c.comments.map((r) => (r.id === id ? change(r) : r)) };
  }
  return c;
});

const toggleLike = (c: Comment): Comment => ({
  ...c,
  isLiked: !c.isLiked,
  likes: Math.max(0, (c.likes || 0) + (c.isLiked ? -1 : 1)),
});

const commentsReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'videoChanged':
      // Another video: its own comments, no reply nor draft in progress.
      return {
        ...state,
        comments: [],
        expanded: [],
        replyTo: null,
        message: '',
        pickedUsers: [],
        mentionToken: null,
        mentionGroup: null,
      };
    case 'pageRequested':
      return { ...state, loading: true };
    case 'commentsLoaded': {
      // The first page replaces the list, the next ones follow it.
      const { docs, hasNextPage, offset } = action;
      const comments = offset === 0 ? docs : [...state.comments, ...docs];
      return {
        ...state,
        comments,
        hasNextPage,
        loading: false,
      };
    }
    case 'loadingEnded':
      // No page in the answer: the spinner goes away, the list stays.
      return { ...state, loading: false };
    case 'likeToggled':
      // Optimistic like, called again to undo it on an error.
      return { ...state, comments: updateComment(state.comments, action.commentId, toggleLike) };
    case 'replyTo': {
      const { parent, receiverId, username } = action;
      return { ...state, replyTo: { parent, receiverId, username } };
    }
    case 'cancelReply':
      return { ...state, replyTo: null };
    case 'repliesToggled': {
      // The replies of a parent are shown or hidden.
      const { parentId } = action;
      if (state.expanded.includes(parentId)) {
        return { ...state, expanded: state.expanded.filter((id) => id !== parentId) };
      }
      return { ...state, expanded: [...state.expanded, parentId] };
    }
    case 'highlight': {
      // A reply is highlighted: its parent is opened first.
      const { commentId, parentId } = action;
      let { expanded } = state;
      if (parentId !== commentId) {
        expanded = addOnce(expanded, parentId);
      }
      return { ...state, expanded, highlighted: commentId };
    }
    case 'highlightEnded':
      return { ...state, highlighted: '' };
    case 'textChanged':
      return { ...state, message: action.text };
    case 'mentionTokenChanged':
      // A new @ (or none): the first suggestion is the selected one.
      return {
        ...state,
        mentionToken: action.token,
        mentionGroup: null,
        activeSuggestion: 0,
      };
    case 'mentionsClosed':
      return { ...state, mentionToken: null, mentionGroup: null };
    case 'activeSuggestionChanged':
      return { ...state, activeSuggestion: action.index };
    case 'activeSuggestionMoved': {
      // The arrows go round the suggestions.
      const { delta, count } = action;
      const activeSuggestion = (state.activeSuggestion + delta + count) % count;
      return { ...state, activeSuggestion };
    }
    case 'mentionToggled': {
      // The user is kept once for the mentioned ids (only those still in the text are sent).
      const { text, user, group } = action;
      let { pickedUsers } = state;
      if (!pickedUsers.some((u) => u.id === user.id)) {
        pickedUsers = [...pickedUsers, user];
      }
      return {
        ...state,
        message: text,
        pickedUsers,
        mentionToken: null,
        mentionGroup: group,
      };
    }
    case 'sendStarted':
      return { ...state, sending: true };
    case 'sendFailed':
      return { ...state, sending: false };
    case 'commentAdded': {
      // A reply goes under its parent (opened), a comment goes first; the draft is cleared.
      const { comment, parentId } = action;
      let { comments, expanded } = state;
      if (parentId) {
        comments = comments.map((c) => (c.id === parentId ? { ...c, comments: [...(c.comments || []), comment] } : c));
        expanded = addOnce(expanded, parentId);
      } else {
        comments = [comment, ...comments];
      }
      return {
        ...state,
        comments,
        expanded,
        sending: false,
        message: '',
        replyTo: null,
        pickedUsers: [],
        mentionToken: null,
        mentionGroup: null,
      };
    }
    default:
      return state;
  }
};

export type {
  Action,
  MentionGroup,
  ReplyTarget,
  State,
};

export { commentsReducer, initialState };
